import { useEffect, useMemo } from 'react';

import { Group } from '@visx/group';
import { hierarchy, Tree } from '@visx/hierarchy';
import { MarkerArrow } from '@visx/marker';
import { LinkHorizontalStep } from '@visx/shape';
import type { ProvidedZoom, TransformMatrix } from '@visx/zoom/lib/types';

import { OrgChartNode } from './OrgChartNode';
import { Employee, NODE_HEIGHT, NODE_WIDTH, PADDING, sampleOrgData } from './constants';

type OrgChartTreeProps = {
  parentWidth: number;
  parentHeight: number;
  zoom: ProvidedZoom<SVGSVGElement> & {
    initialTransformMatrix: TransformMatrix;
    transformMatrix: TransformMatrix;
    isDragging: boolean;
  };
  handleMouseMove: (event: React.MouseEvent, data: Employee) => void;
  hideTooltip: () => void;
};

const rawTree: Employee = sampleOrgData;

export const OrgChartTree = ({ parentWidth, parentHeight, zoom, handleMouseMove, hideTooltip }: OrgChartTreeProps) => {
  const rootData = useMemo(() => hierarchy(rawTree), []);

  const [treeWidth, treeHeight] = useMemo(() => {
    const descendants = rootData.descendants();
    // calculate tree height
    const maxDepth = Math.max(...descendants.map((d) => d.depth));

    // calculate tree width
    const breadthByDepth = new Array(maxDepth + 1).fill(0);
    descendants.forEach((descendant) => {
      breadthByDepth[descendant.depth] += 1;
    });
    const maxBreadth = Math.max(...breadthByDepth);

    const calculatedWidth = (maxDepth + 1) * (NODE_WIDTH + PADDING);
    const calculatedHeight = (maxBreadth + 1) * (NODE_HEIGHT + PADDING);

    return [calculatedWidth, calculatedHeight];
  }, [rootData]);

  useEffect(() => {
    zoom.setTransformMatrix({
      scaleX: 1,
      scaleY: 1,
      translateX: 40, // padding from left
      translateY: (parentHeight - treeHeight) / 2, // center vertically
      skewX: 0,
      skewY: 0,
    });
    // Don't add zoom in dependency array as it will cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentWidth, parentHeight]);

  return (
    <svg
      width={parentWidth}
      height={parentHeight}
      ref={zoom.containerRef}
      style={{
        cursor: zoom.isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
    >
      <MarkerArrow id="arrow" stroke="#CFD7DF" size={6} strokeWidth={1} refX={8} />
      <rect
        width={parentWidth}
        height={parentHeight}
        fill="#fff"
        onTouchStart={zoom.dragStart}
        onTouchMove={zoom.dragMove}
        onTouchEnd={zoom.dragEnd}
        onMouseDown={zoom.dragStart}
        onMouseMove={zoom.dragMove}
        onMouseUp={zoom.dragEnd}
        onMouseLeave={() => {
          if (zoom.isDragging) zoom.dragEnd();
        }}
      />
      <Tree<Employee> root={rootData} size={[treeHeight, treeWidth]}>
        {(tree) => (
          <Group transform={zoom.toString()}>
            {tree.links().map((link, i) => (
              <LinkHorizontalStep
                // eslint-disable-next-line react/no-array-index-key
                key={`link-${i}`}
                data={link}
                stroke="#CFD7DF"
                strokeWidth="1"
                fill="none"
                markerEnd="url(#arrow)"
                source={({ source }) => ({
                  x: source.x,
                  y: source.y + NODE_WIDTH,
                })}
                target={({ target }) => ({
                  x: target.x,
                  y: target.y,
                })}
              />
            ))}
            {tree.descendants().map((node) => (
              <OrgChartNode key={node.data.id} node={node} onMouseMove={handleMouseMove} onMouseLeave={hideTooltip} />
            ))}
          </Group>
        )}
      </Tree>
    </svg>
  );
};
